import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  FunctionOperand,
  LiteralOperand,
  NestedOperand,
  Operand,
  OperandType,
  Operator,
} from '../../../data/models/transportation-benefit-model.model';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControlLabel,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';

const PREFIX = 'TransBenefitOperandEditor';

const classes = {
  container: `${PREFIX}-container`,
  radioGroup: `${PREFIX}-radioGroup`,
  operatorInput: `${PREFIX}-operatorInput`,
  functionSelect: `${PREFIX}-functionSelect`,
  removableFunctionSelect: `${PREFIX}-removableFunctionSelect`,
  addOperandButtonContainer: `${PREFIX}-addOperandButtonContainer`,
  addOperandButton: `${PREFIX}-addOperandButton`,
  removeButton: `${PREFIX}-removeButton`,
  hiddenRemoveButton: `${PREFIX}-hiddenRemoveButton`,
  operatorRemoveButton: `${PREFIX}-operatorRemoveButton`,
  functionRemoveButton: `${PREFIX}-functionRemoveButton`,
  listItem: `${PREFIX}-listItem`,
  removableTypeSelect: `${PREFIX}-removableTypeSelect`,
  readOnlySelect: `${PREFIX}-readOnlySelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    '& .MuiList-padding': {
      padding: 0,
    },
  },
  [`& .${classes.radioGroup}`]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    gap: theme.spacing(1),
    '& .MuiFormControlLabel-label': {
      ...theme.typography.body1,
      paddingBottom: 0,
    },
  },
  [`& .${classes.operatorInput}`]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.addOperandButtonContainer}`]: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
  },
  [`& .${classes.addOperandButton}`]: {
    ...theme.typography.body1,
    paddingLeft: 0,
  },
  [`& .${classes.functionSelect}`]: {
    marginTop: theme.spacing(3),
    flexGrow: 1,
  },
  [`& .${classes.removableFunctionSelect}`]: {
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.removeButton}`]: {
    ...theme.typography.body1,
    marginLeft: theme.spacing(1),
    padding: 0,
    paddingLeft: theme.spacing(1),
  },
  [`& .${classes.hiddenRemoveButton}`]: {
    visibility: 'hidden',
  },
  [`& .${classes.operatorRemoveButton}`]: {
    marginBottom: 20,
  },
  [`& .${classes.functionRemoveButton}`]: {
    marginBottom: 18,
  },
  [`& .${classes.listItem}`]: {
    alignItems: 'start',
    marginTop: theme.spacing(1),
    paddingBottom: 0,
  },
  [`& .${classes.removableTypeSelect}`]: {
    marginRight: 72,
  },
  [`& .${classes.readOnlySelect}`]: {
    '& .MuiInputBase-input': {
      cursor: 'default',
    },
  },
}));

interface TBMOperandProps {
  operand: Operand;
  onChange: (operand: Operand) => void;
  isReadOnly?: boolean;
  isRemovable?: boolean;
  handleRemove?: () => void;
}

export default function TBMOperandEditor({
  operand,
  onChange,
  isReadOnly = false,
  isRemovable,
  handleRemove,
}: TBMOperandProps) {
  const { t } = useTranslation('common');
  const benefitFunctions = useAppSelector(
    clientSlice.selectTransportationBenefitFunctions,
  );

  const type = useMemo<OperandType | null>(() => {
    if (Object.keys(operand).includes('literal')) {
      return OperandType.literal;
    }

    if (Object.keys(operand).includes('function')) {
      return OperandType.function;
    }

    if (Object.keys(operand).includes('operator')) {
      return OperandType.nested;
    }

    return null;
  }, [operand]);

  const functionOptions = useMemo(() => {
    return benefitFunctions
      .filter((f) => f.state === 'active')
      .map((f) => ({
        label: f.name,
        value: f.transportationBenefitFunctionBC,
      }));
  }, [benefitFunctions]);

  const handleTypeChange = (type: string) => {
    if (type === OperandType.literal) {
      onChange({ literal: true });
    }

    if (type === OperandType.function) {
      onChange({ function: '' });
    }

    if (type === OperandType.nested) {
      onChange({ operator: Operator.and, operands: [] });
    }
  };

  const addOperand = () => {
    onChange({
      ...operand,
      operands: [
        ...(operand as NestedOperand).operands,
        { operator: Operator.and, operands: [] },
      ],
    });
  };

  const removeOperand = (index: number) => {
    onChange({
      ...operand,
      operands: (operand as NestedOperand).operands.filter(
        (_, i) => i !== index,
      ),
    });
  };

  const updateOperand = (newOperand: Operand, index: number) => {
    onChange({
      ...operand,
      operands: (operand as NestedOperand).operands.map((o, i) =>
        i === index ? newOperand : o,
      ),
    });
  };

  if (!type) {
    return null;
  }

  const removeButton = (className?: string, isHidden?: boolean) => (
    <Button
      className={`${classes.removeButton} ${className ?? ''} ${isHidden ? classes.hiddenRemoveButton : ''}`}
      onClick={handleRemove}>
      {t('common.remove')}
    </Button>
  );

  return (
    <StyledBox className={classes.container}>
      <TextField
        select
        className={`${isRemovable ? classes.removableTypeSelect : ''} ${isReadOnly ? classes.readOnlySelect : ''}`}
        variant="outlined"
        required
        value={type}
        onChange={(evt) => handleTypeChange(evt.target.value)}
        InputProps={{
          readOnly: isReadOnly,
        }}
        label={t('transBenefitModel.transBenefitModelType')}>
        <MenuItem value={OperandType.literal}>
          {t('transBenefitModel.literal')}
        </MenuItem>
        <MenuItem value={OperandType.function}>
          {t('transBenefitModel.function')}
        </MenuItem>
        <MenuItem value={OperandType.nested}>
          {t('transBenefitModel.operand')}
        </MenuItem>
      </TextField>
      {type === OperandType.literal && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between">
          <RadioGroup
            className={classes.radioGroup}
            value={(operand as LiteralOperand).literal}
            onChange={(evt, value) => onChange({ literal: value === 'true' })}>
            <FormControlLabel
              value="true"
              disabled={isReadOnly}
              control={<Radio />}
              label={t('common.true')}
            />
            <FormControlLabel
              value="false"
              disabled={isReadOnly}
              control={<Radio />}
              label={t('common.false')}
            />
          </RadioGroup>
          {isRemovable && removeButton(undefined, isReadOnly)}
        </Stack>
      )}
      {type === OperandType.function && (
        <Stack direction="row" alignItems="end">
          <TextField
            select
            required
            variant="outlined"
            className={`${classes.functionSelect} ${isRemovable ? classes.removableFunctionSelect : ''} ${isReadOnly ? classes.readOnlySelect : ''}`}
            value={(operand as FunctionOperand).function}
            onChange={(evt) => onChange({ function: evt.target.value })}
            InputProps={{
              readOnly: isReadOnly,
            }}
            label={t('transBenefitModel.option')}>
            {functionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {isRemovable &&
            removeButton(classes.functionRemoveButton, isReadOnly)}
        </Stack>
      )}
      {type === OperandType.nested && (
        <>
          <Stack direction="row" alignItems="end">
            <TextField
              select
              required
              className={`${classes.operatorInput} ${isReadOnly ? classes.readOnlySelect : ''}`}
              variant="outlined"
              value={(operand as NestedOperand).operator}
              InputProps={{
                readOnly: isReadOnly,
              }}
              onChange={(evt) =>
                onChange({
                  ...operand,
                  operator: evt.target.value as Operator,
                })
              }
              label={t('transBenefitModel.operator')}>
              <MenuItem value={Operator.and}>
                {t('transBenefitModel.and')}
              </MenuItem>
              <MenuItem value={Operator.or}>
                {t('transBenefitModel.or')}
              </MenuItem>
              <MenuItem value={Operator.not}>
                {t('transBenefitModel.not')}
              </MenuItem>
            </TextField>
            {isRemovable &&
              removeButton(classes.operatorRemoveButton, isReadOnly)}
          </Stack>

          <List>
            {(operand as NestedOperand).operands.map((operand, index) => (
              <ListItem key={index} className={classes.listItem}>
                <TBMOperandEditor
                  operand={operand}
                  onChange={(newOperand) => updateOperand(newOperand, index)}
                  isRemovable={true}
                  handleRemove={() => removeOperand(index)}
                  isReadOnly={isReadOnly}
                />
              </ListItem>
            ))}
          </List>
          {!isReadOnly && (
            <Box className={classes.addOperandButtonContainer}>
              <Button className={classes.addOperandButton} onClick={addOperand}>
                + {t('transBenefitModel.addOperand')}
              </Button>
            </Box>
          )}
        </>
      )}
    </StyledBox>
  );
}
